import { useState, useEffect, useRef } from 'preact/hooks';
import { render } from 'preact';
import { store, type Toast } from '../store';

const Portal = ({ children }: { children: import('preact').ComponentChildren }) => {
  const containerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = document.createElement('div');
    el.className = 'shop-widget-mount shop-widget-portal';
    containerRef.current = el;
    document.body.appendChild(el);
    return () => {
      if (containerRef.current) {
        render(null, containerRef.current);
        document.body.removeChild(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      render(children, containerRef.current);
    }
  }, [children]);

  return null;
};

const DEFAULT_TOP = 90;
const GAP = 12;

function useHeaderOffset(): number {
  const [top, setTop] = useState(DEFAULT_TOP);
  useEffect(() => {
    const header = document.querySelector('.sl-header, .sl-header-topbar, header[class*="sl-"]');
    const measure = () => {
      const h = header as HTMLElement;
      setTop(h ? h.offsetHeight + GAP : DEFAULT_TOP);
    };
    measure();
    if (!header) return;
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(header);
    return () => ro?.disconnect();
  }, []);
  return top;
}

export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>(store.getToasts());
  const topPx = useHeaderOffset();

  useEffect(() => {
    return store.subscribe(() => setToasts(store.getToasts()));
  }, []);

  if (toasts.length === 0) return null;

  return (
    <Portal>
      <div style={{ position: 'fixed', top: `${topPx}px`, right: '20px', zIndex: 1060, display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {toasts.map((toast) => {
          const bgColor = toast.type === 'error' ? '#dc3545' : toast.type === 'success' ? '#198754' : '#0d6efd';
          return (
            <div 
              key={toast.id} 
              class={`toast show align-items-center text-white border-0`} 
              role="alert" 
              aria-live="assertive" 
              aria-atomic="true"
              style={{ minWidth: '300px', opacity: 1, display: 'block', backgroundColor: bgColor }}
            >
              <div class="d-flex align-items-center justify-content-between w-100" style={{ padding: '0.75rem' }}>
                <div class="d-flex align-items-center" style={{ color: '#fff' }}>
                  {toast.type === 'success' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginRight: '.5rem' }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                      <path d="M8 12.5l2.5 2.5L16 9.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  )}
                  {toast.type === 'error' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginRight: '.5rem' }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                      <path d="M12 7v6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                      <circle cx="12" cy="16.5" r="1" fill="currentColor" />
                    </svg>
                  )}
                  {toast.type === 'info' && (
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" style={{ marginRight: '.5rem' }}>
                      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8" />
                      <path d="M12 11v5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
                      <circle cx="12" cy="8" r="1" fill="currentColor" />
                    </svg>
                  )}
                  <span class="fw-semibold">{toast.message}</span>
                </div>
                <button 
                  type="button" 
                  class="btn-close btn-close-white ms-3" 
                  data-bs-dismiss="toast" 
                  aria-label="Close"
                  style={{ filter: 'invert(1) grayscale(100%) brightness(200%)' }}
                  onClick={() => {}}
                ></button>
              </div>
            </div>
          );
        })}
      </div>
    </Portal>
  );
}
